import { useEffect, useState, useCallback } from "react";
import { RefreshCw, ExternalLink, Link2, CheckCircle2, AlertTriangle, Copy } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { PageHeader } from "../../components/dashboard/Shared";

const Stat = ({ label, value, accent }) => (
  <div className="card-dark p-4">
    <p className="label-xs">{label}</p>
    <p className={`tabular text-lg mt-1 ${accent || "text-white"}`} data-testid={`gs-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>{value}</p>
  </div>
);

export default function GoogleSheets() {
  const [status, setStatus] = useState(null);
  const [sid, setSid] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/gsheets/status");
    setStatus(data);
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const act = async (label, fn) => {
    setBusy(label);
    try { const r = await fn(); if (r?.data?.ok === false && r?.data?.error) toast.error(r.data.error); else toast.success(`${label} done`); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.response?.data?.error || `${label} failed`); }
    setBusy("");
  };

  if (!status) return <div className="skeleton h-40" />;
  const cfg = status.config || {};
  const connected = !!cfg.spreadsheet_id;

  return (
    <div data-testid="admin-gsheets-page">
      <PageHeader title="Google Sheets" sub="Automatic ChartzFx tracking-history reporting (MongoDB stays the source of truth)">
        <button data-testid="gs-refresh" onClick={load} className="btn-outline px-3 py-1.5 text-xs flex items-center gap-2"><RefreshCw size={12} /> Refresh</button>
      </PageHeader>

      {!status.is_production && (
        <div className="card-dark p-4 mb-5 border border-[#FFD600]/40 flex items-start gap-3" data-testid="gs-env-banner">
          <AlertTriangle size={16} className="text-[#FFD600] mt-0.5 shrink-0" />
          <div>
            <p className="text-sm text-[#FFD600]">Preview / non-production environment — sheet writes are blocked</p>
            <p className="text-xs text-gray-400 mt-1" data-testid="gs-env-reason">{status.sync_blocked_reason || "Google Sheets sync runs only in production (Atlas). Deploy and set the GOOGLE_* env vars in production to enable live sync."}</p>
            <p className="text-[11px] text-gray-600 mt-1">Environment: <span className="text-gray-300">{status.environment}</span></p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Stat label="Status" value={!status.is_production ? "PREVIEW" : (connected ? "CONNECTED" : "NOT CONNECTED")}
          accent={!status.is_production ? "text-[#FFD600]" : (connected ? "text-[#00E676]" : "text-[#FF3B30]")} />
        <Stat label="Sync" value={status.enabled ? "ENABLED" : (status.is_production ? "DISABLED" : "PAUSED (PREVIEW)")} accent={status.enabled ? "text-[#00E676]" : "text-gray-400"} />
        <Stat label="Pending Records" value={status.pending ?? 0} accent="text-[#FFD600]" />
        <Stat label="Failed Records" value={status.failed ?? 0} accent={status.failed ? "text-[#FF3B30]" : "text-white"} />
      </div>

      {!connected && (
        <div className="card-dark p-5 mb-5 border border-[#FFD600]/30" data-testid="gs-setup">
          <div className="flex items-center gap-2 mb-3 text-[#FFD600]"><AlertTriangle size={16} /><p className="text-sm">Connect a spreadsheet</p></div>
          <p className="text-xs text-gray-400 mb-3 leading-relaxed">
            A service account can't create files in Drive. Create a blank Google Sheet, click <b>Share</b>, and give
            <b> Editor</b> access to the service-account email below. Then paste the sheet's URL or ID and connect.
          </p>
          <div className="flex items-center gap-2 mb-4 bg-white/[0.03] rounded-lg px-3 py-2">
            <code className="text-[11px] text-[#00F0FF] break-all flex-1" data-testid="gs-service-email">{status.service_email || "—"}</code>
            <button onClick={() => { navigator.clipboard.writeText(status.service_email || ""); toast.success("Copied"); }} className="text-gray-400 hover:text-white"><Copy size={13} /></button>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <input data-testid="gs-sid-input" value={sid} onChange={(e) => setSid(e.target.value)} placeholder="Paste Google Sheet URL or Spreadsheet ID"
              className="input-dark !py-2 text-xs flex-1" />
            <button data-testid="gs-connect-btn" disabled={!sid || busy} onClick={() => act("Connect", () => api.post("/gsheets/spreadsheet-id", { spreadsheet_id: sid }))}
              className="btn-primary px-4 py-2 text-xs flex items-center gap-2 disabled:opacity-50"><Link2 size={13} /> Connect</button>
          </div>
        </div>
      )}

      {connected && (
        <div className="card-dark p-5 mb-5">
          <div className="flex items-center gap-2 mb-3 text-[#00E676]"><CheckCircle2 size={16} /><p className="text-sm">Spreadsheet connected</p></div>
          <a data-testid="gs-open-link" href={cfg.url} target="_blank" rel="noreferrer" className="text-xs text-[#00F0FF] hover:underline inline-flex items-center gap-1">{cfg.url} <ExternalLink size={11} /></a>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 mt-4 text-xs">
            <div><span className="text-gray-500">Last successful sync</span><p className="text-white">{status.last_ok_at ? new Date(status.last_ok_at).toLocaleString() : "—"}</p></div>
            <div><span className="text-gray-500">Last failed sync</span><p className="text-white">{status.last_err_at ? new Date(status.last_err_at).toLocaleString() : "—"}</p></div>
            <div><span className="text-gray-500">API status</span><p className="text-white">{status.api_status || "IDLE"}</p></div>
          </div>
          {status.last_error && <p className="text-[11px] text-[#FF3B30] mt-2">Last error: {status.last_error}</p>}
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <button data-testid="gs-sync-now" disabled={!connected || busy} onClick={() => act("Sync Now", () => api.post("/gsheets/sync-now"))}
          className="btn-primary px-4 py-2 text-xs disabled:opacity-50">{busy === "Sync Now" ? "Syncing…" : "Sync Now"}</button>
        <button data-testid="gs-retry" disabled={busy} onClick={() => act("Retry Failed", () => api.post("/gsheets/retry"))} className="btn-outline px-4 py-2 text-xs disabled:opacity-50">Retry Failed</button>
        <button data-testid="gs-toggle" disabled={busy} onClick={() => act(status.enabled ? "Disable" : "Enable", () => api.post("/gsheets/toggle", { enabled: !status.enabled }))}
          className="btn-outline px-4 py-2 text-xs disabled:opacity-50">{status.enabled ? "Disable Sync" : "Enable Sync"}</button>
        {connected && <button data-testid="gs-disconnect" disabled={busy} onClick={() => act("Disconnect", () => api.post("/gsheets/disconnect"))} className="px-4 py-2 text-xs rounded-lg border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10">Disconnect</button>}
        {connected && <button data-testid="gs-purge" disabled={busy} onClick={() => { if (window.confirm("Clear ALL data rows from every tab (headers kept)? Used to remove preview/test rows before a clean production backfill.")) act("Purge data", () => api.post("/gsheets/purge-data")); }} className="px-4 py-2 text-xs rounded-lg border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10">Purge Data Rows</button>}
      </div>
    </div>
  );
}
